import React from 'react';
import { Typography, Grid, Divider, Card, CardContent, Box, Chip } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Assignment } from '@mui/icons-material';
import SchoolIcon from '@mui/icons-material/School';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

const formatPeriod = (period) => {
    const DAYS_MAP = {
        0: t('planViewer.days.0'),
        1: t('planViewer.days.1'),
        2: t('planViewer.days.2'),
        3: t('planViewer.days.3'),
        4: t('planViewer.days.4'),
        5: t('planViewer.days.5'),
        6: t('planViewer.days.6')
    };

    try {
        const [minute, hour, , , days] = period.split(' ');
        const daysList = days?.split(',').map((d) => DAYS_MAP[d]) || [];
        if (!daysList.length || daysList.length === 7)
            return t('planViewer.all_days_at', { hour, minute });
        if (daysList.length === 1) return `${daysList[0]} às ${hour}h${minute}`;
        const lastDay = daysList.pop();
        return `${daysList.join(', ')} e ${lastDay} às ${hour}h${minute}`;
    } catch {
        return t('planViewer.invalid_period');
    }
};

const AEROBIC_TYPES = [
    'rehabilitation',
    'treadmill',
    'ergometer',
    'walking',
    'running',
    'cycling',
    'rest',
    'other'
];

const RESISTANCE_TYPES = [
    'squat',
    'sit_to_stand',
    'push',
    'pull',
    'arm_raise',
    'knee_extension'
];

const PlanViewer = ({
    sessions,
    questionnaires,
    initialQuestionnaires,
    learningContents,
    initialLearningContents
}) => {
    const { t } = useTranslation();

    const TYPE_LABELS = {
        rehabilitation: t('exercise.types.rehabilitation'),
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        easy: t('exercise.types.easy'),
        other: t('exercise.types.other')
    };

    const RESISTANCE_LABELS = {
        squat: t('exercise.types.squat'),
        sit_to_stand: t('exercise.types.sit_to_stand'),
        push: t('exercise.types.push'),
        pull: t('exercise.types.pull'),
        arm_raise: t('exercise.types.arm_raise'),
        knee_extension: t('exercise.types.knee_extension')
    };

    const isAerobic = (exercise) => AEROBIC_TYPES.includes(exercise.type);
    const isResistance = (exercise) => RESISTANCE_TYPES.includes(exercise.type);

    const getExerciseLabel = (exercise) => {
        if (isAerobic(exercise)) {
            return TYPE_LABELS[exercise.type] || exercise.type;
        }

        if (isResistance(exercise)) {
            return RESISTANCE_LABELS[exercise.type] || exercise.type;
        }

        return exercise.type;
    };

    const renderExerciseChip = (exercise) => {
        if (isAerobic(exercise)) {
            const minutes = exercise.duration ? Math.round(exercise.duration / 60) : 0;

            return (
                <Chip
                    icon={<AccessTimeIcon />}
                    label={`${minutes} min`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            );
        }

        if (isResistance(exercise)) {
            return (
                <Chip
                    icon={<FitnessCenterIcon />}
                    label={`${exercise.sets ?? '-'} x ${exercise.reps ?? '-'}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                />
            );
        }

        return null;
    };

    return (
        <Grid container direction="column" spacing={2} sx={{ mt: 1 }}>
            {sessions?.map((session) => (
                <Card
                    key={session.id}
                    variant="outlined"
                    sx={{
                        borderRadius: 3,
                        boxShadow: 1,
                        backgroundColor: '#fff',
                        mt: 2,
                        ml: 2,
                        overflow: 'hidden'
                    }}
                >
                    {/* Session Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.2,
                            bgcolor: '#1976d2',
                            color: 'white',
                            px: 2,
                            py: 1.2
                        }}
                    >
                        <CalendarTodayIcon fontSize="small" />
                        <Typography variant="h6" fontWeight="600" color="white">
                            {session.name}
                        </Typography>
                    </Box>

                    <CardContent sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {session.description || t('planViewer.no_description')}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                            {formatPeriod(session.period)}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle1" gutterBottom>
                            <FitnessCenterIcon
                                fontSize="small"
                                sx={{ mr: 0.5, verticalAlign: 'middle' }}
                            />
                            {t('planViewer.exercises')}
                        </Typography>

                        <Grid container spacing={2}>
                            {session.exercises?.map((exercise) => (
                                <Grid item xs={12} sm={6} md={4} key={exercise.id}>
                                    <Box
                                        sx={{
                                            border: '1px solid #eee',
                                            borderRadius: 2,
                                            p: 2,
                                            backgroundColor: '#fafafa',
                                            transition: '0.2s',
                                            '&:hover': { backgroundColor: '#f5f5f5' }
                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {exercise.order}. {getExerciseLabel(exercise)}
                                        </Typography>

                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            {exercise.description ||
                                                t('planViewer.no_description')}
                                        </Typography>

                                        {renderExerciseChip(exercise)}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            ))}

            <Grid container spacing={2} mt={2} alignItems="stretch">
                {(initialQuestionnaires?.length > 0 || questionnaires?.length > 0) && (
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 3,
                                boxShadow: 1,
                                backgroundColor: '#fff',
                                overflow: 'hidden',
                                ml: 2,
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.2,
                                    bgcolor: '#1976d2',
                                    color: 'white',
                                    px: 2,
                                    py: 1.2
                                }}
                            >
                                <Assignment fontSize="small" />
                                <Typography variant="h6" fontWeight="600" color="white">
                                    {t('plans.questionnaires.title')}
                                </Typography>
                            </Box>

                            <CardContent sx={{ flex: 1 }}>
                                {/* Questionários iniciais */}
                                {initialQuestionnaires?.length > 0 && (
                                    <>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t('plans.questionnaires.initial')}
                                        </Typography>

                                        {initialQuestionnaires.map((q) => (
                                            <Typography
                                                key={q.questionnaireUuid}
                                                variant="body2"
                                                sx={{ ml: 1 }}
                                            >
                                                • {q.questionnaireName}
                                            </Typography>
                                        ))}

                                        <Divider sx={{ my: 1.5 }} />
                                    </>
                                )}

                                {/* Questionários agendados */}
                                {questionnaires?.length > 0 ? (
                                    <>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t('plans.questionnaires.scheduled')}
                                        </Typography>

                                        {questionnaires.map((q) => (
                                            <Box key={q.questionnaireUuid} mb={1}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="600"
                                                >
                                                    {q.questionnaireName}
                                                </Typography>
                                                <Chip
                                                    label={
                                                        Array.isArray(q.assignedWeeks) &&
                                                        q.assignedWeeks.length > 0
                                                            ? `${t('planViewer.weeks')}: ${[
                                                                  ...q.assignedWeeks
                                                              ]
                                                                  .sort((a, b) => a - b)
                                                                  .join(', ')}`
                                                            : t('planViewer.not_assigned')
                                                    }
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        ))}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('plans.questionnaires.none')}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {(initialLearningContents?.length > 0 ||
                    learningContents?.length > 0) && (
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 3,
                                boxShadow: 1,
                                backgroundColor: '#fff',
                                overflow: 'hidden',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.2,
                                    bgcolor: '#1976d2',
                                    color: 'white',
                                    px: 2,
                                    py: 1.2
                                }}
                            >
                                <SchoolIcon fontSize="small" />
                                <Typography variant="h6" fontWeight="600" color="white">
                                    {t('plans.learningContents.title')}
                                </Typography>
                            </Box>

                            <CardContent sx={{ flex: 1 }}>
                                {/* Conteúdos iniciais */}
                                {initialLearningContents?.length > 0 && (
                                    <>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t('plans.learningContents.initial')}
                                        </Typography>

                                        {initialLearningContents.map((c) => (
                                            <Typography
                                                key={c.contentUuid}
                                                variant="body2"
                                                sx={{ ml: 1 }}
                                            >
                                                • {c.contentTitle}
                                            </Typography>
                                        ))}

                                        <Divider sx={{ my: 1.5 }} />
                                    </>
                                )}

                                {/* Conteúdos agendados */}
                                {learningContents?.length > 0 ? (
                                    <>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t('plans.questionnaires.scheduled')}
                                        </Typography>

                                        {learningContents.map((c) => (
                                            <Box key={c.contentUuid} mb={1}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="500"
                                                >
                                                    • {c.contentTitle}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {t('planViewer.week')}:{' '}
                                                    <strong>
                                                        {c.assignedWeek ??
                                                            t('planViewer.not_assigned')}
                                                    </strong>
                                                </Typography>
                                            </Box>
                                        ))}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('plans.learningContents.none')}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default PlanViewer;
